import { graphql } from 'react-relay';
import { useState, useEffect } from 'react';

import { isExperimentEnabled } from '@confluence/feature-experiments';

import type {
	useLabelsLabelCountQuery$data,
	useLabelsLabelCountQuery$variables,
} from './__generated__/useLabelsLabelCountQuery.graphql.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { useLabelsFragment } from './useLabelsFragment.ts';

export const useLabels = (contentId: string): boolean => {
	const [hasLabelsLegacy, setHasLabelsLegacy] = useState<boolean>(true);
	const editorFragmentResult = useLabelsFragment(contentId);
	const queryResult = useQueryInterop<
		useLabelsLabelCountQuery$data,
		useLabelsLabelCountQuery$variables
	>(
		graphql`
			query useLabelsLabelCountQuery($contentId: ID!) {
				content(id: $contentId, status: ["current", "draft"]) {
					nodes {
						id
						labels(orderBy: { sortField: LABELLING_CREATIONDATE, direction: ASCENDING }) {
							count
						}
					}
				}
			}
		`,
		{
			variables: { contentId },
			skip: editorFragmentResult.isProvided,
		},
	);

	const { data, loading } = editorFragmentResult.isProvided ? editorFragmentResult : queryResult;
	const hasLabels = !loading && (data?.content?.nodes?.[0]?.labels?.count ?? 0) > 0;

	useEffect(() => {
		if (!isExperimentEnabled('confluence_editor_content_unified_query')) {
			setHasLabelsLegacy(hasLabels);
		}
	}, [hasLabels]);

	return isExperimentEnabled('confluence_editor_content_unified_query')
		? hasLabels
		: hasLabelsLegacy;
};
